import { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, User, Menu, X, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/buddy/luna-the-unicorn", label: "Shop" },
  { section: "find-buddy-section", label: "Meet the Buddies" },
  { section: "how-it-works-section", label: "How It Works" },
  { section: "stories-section", label: "Stories" },
  { section: "app-section", label: "For Parents" },
  { section: "compare-section", label: "Why Us" },
  { to: "/our-story", label: "Our Story" },
];

export default function Header() {
  const { count, setDrawerOpen } = useCart();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const overlayHero = pathname === "/";
  const isPDP = pathname.startsWith("/buddy/");

  const goSection = (sectionId) => {
    setOpen(false);
    if (pathname === "/") {
      document.querySelector(`[data-testid="${sectionId}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/", { state: { scrollTo: sectionId } });
    }
  };

  const navItemClass = "text-[13px] font-semibold tracking-wide whitespace-nowrap transition-colors text-cloud/75 hover:text-moon";

  return (
    <header className={`${overlayHero ? "absolute" : "sticky"} inset-x-0 top-0 z-50 ${overlayHero ? "bg-transparent" : "bg-navy"}`} data-testid="site-header">
      <div className="relative mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 py-2.5">
        <Link to="/" data-testid="header-logo" className="flex items-center shrink-0" aria-label="Bedtime Buddies home">
          {!overlayHero && (
            <img src="/assets/logo-horizontal.webp" alt="Bedtime Buddies" className={isPDP ? "h-10 w-auto" : "hidden h-11 w-auto lg:block"} />
          )}
          <span className={`font-display text-lg font-semibold tracking-wide text-white ${overlayHero ? "invisible" : isPDP ? "hidden" : "lg:hidden"}`}>Bedtime Buddies<span className="text-pink">™</span></span>
        </Link>

        <nav className="hidden lg:flex absolute left-1/2 -translate-x-1/2 items-center gap-6" aria-label="Main navigation">
          {NAV.map((n) => (
            n.section ? (
              <button key={n.label} type="button" onClick={() => goSection(n.section)}
                data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={navItemClass}>
                {n.label}
              </button>
            ) : (
              <NavLink key={n.label} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) => `text-[13px] font-semibold tracking-wide whitespace-nowrap transition-colors hover:text-moon ${isActive ? "text-moon" : "text-cloud/75"}`}>
                {n.label}
              </NavLink>
            )
          ))}
        </nav>

        <div className="flex items-center gap-1">
          <button data-testid="header-search-btn" aria-label="Search"
            className="hidden sm:flex h-9 w-9 items-center justify-center rounded-full text-cloud/70 transition hover:bg-white/10 hover:text-cloud">
            <Search size={17} />
          </button>
          <button data-testid="header-account-btn" aria-label="Account" onClick={() => navigate(user ? "/account" : "/login")}
            className="hidden sm:flex h-9 w-9 items-center justify-center rounded-full text-cloud/70 transition hover:bg-white/10 hover:text-cloud">
            <User size={17} />
          </button>
          <button data-testid="header-cart-btn" aria-label={`Cart, ${count} items`} onClick={() => setDrawerOpen(true)}
            className="relative flex h-9 w-9 items-center justify-center rounded-full text-cloud/80 transition hover:bg-white/10 hover:text-cloud">
            <ShoppingBag size={18} />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 flex h-4.5 h-5 w-5 items-center justify-center rounded-full bg-pink font-display text-[10px] font-semibold text-navy">
                {count}
              </span>
            )}
          </button>
          <Link to="/buddy/luna-the-unicorn" data-testid="header-find-buddy-cta"
            className="ml-1.5 hidden md:inline-flex items-center rounded-full bg-pink px-4.5 px-5 py-2 font-display text-[13px] font-semibold tracking-wide text-navy transition hover:scale-[1.04] hover:bg-moon">
            FIND THEIR BUDDY
          </Link>
          <button data-testid="mobile-menu-btn" aria-label="Open menu" onClick={() => setOpen(true)}
            className="flex h-9 w-9 items-center justify-center rounded-full text-cloud lg:hidden">
            <Menu size={21} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-cream lg:hidden" data-testid="mobile-menu">
            <div className="flex items-center justify-between bg-navy px-4 py-2.5">
              <span className="font-display text-lg font-semibold tracking-wide text-white">Bedtime Buddies<span className="text-pink">™</span></span>
              <button data-testid="mobile-menu-close" aria-label="Close menu" onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full text-cloud">
                <X size={24} />
              </button>
            </div>
            <nav className="flex flex-col gap-1 px-8 pt-10" aria-label="Mobile navigation">
              {NAV.map((n, i) => (
                <motion.div key={n.label} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  {n.section ? (
                    <button type="button" onClick={() => goSection(n.section)}
                      data-testid={`mobile-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                      className="block py-3 font-display text-3xl font-medium text-navy transition hover:text-pink">
                      {n.label}
                    </button>
                  ) : (
                    <NavLink to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                      className="block py-3 font-display text-3xl font-medium text-navy transition hover:text-pink">
                      {n.label}
                    </NavLink>
                  )}
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="pt-6">
                <Link to="/buddy/luna-the-unicorn" onClick={() => setOpen(false)} data-testid="mobile-find-buddy-cta"
                  className="inline-flex rounded-full bg-pink px-8 py-4 font-display text-lg font-semibold text-navy">
                  FIND THEIR BUDDY
                </Link>
                <Link to={user ? "/account" : "/login"} onClick={() => setOpen(false)} data-testid="mobile-account-link"
                  className="mt-4 block text-navy/60 font-semibold">
                  {user ? `Hi, ${user.name}` : "Sign in"}
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
